import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Text } from "../../../../components/common/typography";
import { Chip } from "../../../../components/common/chip";
import { SimpleTable, type SimpleTableColumn } from "../../../../components/common/simple-table";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer";
import { Pagination } from "../../../../components/common/pagination";
import { Button } from "../../../../components/common/button";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { usePartnerPayments } from "../../hooks/usePartnerPayments";
import { PaymentDirection, PaymentState, type PaymentDto } from "../../../payments/data/types";
import { dollarFormat } from "../../../../utils/currencies";

const PAGE_SIZE = 10;

const DATE_FMT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}

function PaymentStateChip({ state }: { state: PaymentState }) {
  if (state === PaymentState.PAID) return <Chip variant="approved">Paid</Chip>;
  if (state === PaymentState.POSTED) return <Chip variant="inReview">Validated</Chip>;
  if (state === PaymentState.IN_PROCESS) return <Chip variant="underReview">In Process</Chip>;
  if (state === PaymentState.CANCELLED || state === PaymentState.REJECTED) return <Chip variant="rejected">{state === PaymentState.CANCELLED ? "Cancelled" : "Rejected"}</Chip>;
  return <Chip variant="tertiary">Draft</Chip>;
}

interface PartnerPaymentsTabProps {
  partnerId: number;
}

export function PartnerPaymentsTab({ partnerId }: PartnerPaymentsTabProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading } = usePartnerPayments(partnerId, { page, pageSize: PAGE_SIZE });

  const columns = useMemo((): SimpleTableColumn<PaymentDto>[] => [
    {
      key: "date",
      label: "Date",
      className: "flex-1",
      render: (row) => <Text variant="body" tone="body">{fmtDate(row.date)}</Text>,
    },
    {
      key: "direction",
      label: "Direction",
      className: "flex-1",
      render: (row) => (
        <Text variant="body" tone={row.direction === PaymentDirection.INCOMING ? "greenDark" : "body"}>
          {row.direction === PaymentDirection.INCOMING ? "Incoming" : "Outgoing"}
        </Text>
      ),
    },
    {
      key: "amount",
      label: "Amount",
      className: "flex-1",
      render: (row) => (
        <Text variant="bodyBold" tone="dark">
          {dollarFormat(row.amount, row.currency ?? "QAR")}
        </Text>
      ),
    },
    {
      key: "reference",
      label: "Reference",
      className: "flex-[2] min-w-0",
      render: (row) => (
        <Text variant="body" tone="body" className="truncate">
          {row.reference ?? row.memo ?? "—"}
        </Text>
      ),
    },
    {
      key: "status",
      label: "Status",
      className: "flex-1",
      render: (row) => <PaymentStateChip state={row.state} />,
    },
    {
      key: "actions",
      label: "Actions",
      className: "flex-1 flex justify-end",
      render: (row) => (
        <Button variant="ghost" size="sm" className="!px-0 !text-textDark"
          onClick={(e) => { e.stopPropagation(); navigate(AppRoutes.ADMIN_PAYMENT_DETAILS.replace(":id", String(row.id))); }}>
          View
        </Button>
      ),
    },
  ], [navigate]);

  const pageCount = data?.meta?.pageCount ?? 1;

  if (isLoading) {
    return (
      <ShimmerLoader message="Fetching payments…">
        <div className="flex flex-col gap-2">{Array.from({ length: 6 }).map((_, i) => <Shimmer key={i} className="h-12 w-full" />)}</div>
      </ShimmerLoader>
    );
  }

  return (
    <SimpleTable
      columns={columns}
      data={data?.data ?? []}
      keyExtractor={(p) => String(p.id)}
      onRowClick={(p) => navigate(AppRoutes.ADMIN_PAYMENT_DETAILS.replace(":id", String(p.id)))}
      footer={pageCount > 1 ? (
        <div className="flex justify-center"><Pagination page={page} pageCount={pageCount} onPageChange={setPage} /></div>
      ) : undefined}
    />
  );
}
